import { useAuth } from "@clerk/clerk-react";

// API base URL - pulled from environment variables
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api";

/**
 * Custom hook for making authenticated API calls
 */
export function useApi() {
  const { getToken } = useAuth();

  /**
   * Generic fetch wrapper with error handling
   */
  async function fetchWithErrorHandling<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const token = await getToken();

      console.log(token);

      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
            `API error: ${response.status} ${response.statusText}`
        );
      }

      // For 204 No Content responses
      if (response.status === 204) {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  /**
   * API Service with methods for common operations
   */
  const apiService = {
    /**
     * Get resource
     */
    async get<T>(endpoint: string): Promise<T> {
      return fetchWithErrorHandling<T>(`${API_BASE_URL}${endpoint}`);
    },

    /**
     * Create resource
     */
    async post<T, U = Record<string, unknown>>(
      endpoint: string,
      data: U
    ): Promise<T> {
      return fetchWithErrorHandling<T>(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        body: JSON.stringify(data),
      });
    },

    /**
     * Update resource
     */
    async put<T, U = Record<string, unknown>>(
      endpoint: string,
      data: U
    ): Promise<T> {
      return fetchWithErrorHandling<T>(`${API_BASE_URL}${endpoint}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },

    /**
     * Partial update resource
     */
    async patch<T, U = Record<string, unknown>>(
      endpoint: string,
      data: U
    ): Promise<T> {
      return fetchWithErrorHandling<T>(`${API_BASE_URL}${endpoint}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete resource
     */
    async delete<T = void>(endpoint: string): Promise<T> {
      return fetchWithErrorHandling<T>(`${API_BASE_URL}${endpoint}`, {
        method: "DELETE",
      });
    },
  };

  return apiService;
}
